import { useCallback, useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NormativaLink } from "@/components/shared/NormativaLink";
import { getLegendOptionsForIndustry } from "@/lib/fiscalProfileLegendOptions";
import type { FiscalProfileEditorValues } from "@/lib/fiscalProfileEditorTypes";
import { inferExonerationSuggestion } from "@/lib/inferExonerationSuggestion";

export interface SpecialRegimesSectionProps {
  idPrefix: string;
  industry: string | null;
}

export function SpecialRegimesSection({
  idPrefix,
  industry,
}: SpecialRegimesSectionProps): JSX.Element {
  const { t } = useTranslation();
  const legendInputRef = useRef<HTMLInputElement>(null);
  const { watch, setValue, control } = useFormContext<FiscalProfileEditorValues>();

  const hasExoneration = watch("hasExoneration");
  const exonerationLegendId = watch("exonerationLegendId");
  const lineLegend = watch("exonerationLineLegend");

  const options = useMemo(
    () => getLegendOptionsForIndustry(industry),
    [industry]
  );

  const isExporting = watch("isExportingServices");
  const suggestion = useMemo(
    () => inferExonerationSuggestion(industry, isExporting),
    [industry, isExporting]
  );

  const onLegendIdChange = useCallback(
    (value: string) => {
      setValue("exonerationLegendId", value);
      const option = options.find((o) => o.id === value);
      if (option?.legendText != null) {
        setValue("exonerationLineLegend", option.legendText);
        setValue("exonerationHeaderLegend", option.legendText);
      } else {
        setValue("exonerationLineLegend", "");
        setValue("exonerationHeaderLegend", "");
        requestAnimationFrame(() => {
          legendInputRef.current?.focus();
        });
      }
    },
    [options, setValue]
  );

  const dropdownValue = options.some((o) => o.id === exonerationLegendId)
    ? exonerationLegendId
    : "custom";

  const hintKey = suggestion?.hintKey;

  return (
    <>
      {/* Two cards, not one. These are unrelated settings that only had in
          common that both are optional: the IVA regime decides which RATES the
          invoice line offers, the leyendas decide what TEXT lands on the
          invoice. Sharing a card put the leyenda "Sugerencia" banner directly
          under the IVA block, where it read as advice about the IVA rate. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-foreground">
            {t(
              "settings.fiscalProfile.sections.ivaRegimeTitle",
              "Régimen especial de IVA (opcional)",
            )}
          </CardTitle>
          <CardDescription>
            {t(
              "settings.fiscalProfile.sections.ivaRegimeDescription",
              "Solo si la empresa factura con una tasa de IVA propia, como las automotoras.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
        {/* Régimen especial de IVA — a SECOND axis, not a variant of the IVA
            regime (an automotora is still CEDE or NO CEDE), and NOT the same
            thing as the "régimen exonerado" checkbox below, which only adds a
            leyenda. The label says "de IVA" precisely because that checkbox
            also contains the words "régimen especial". */}
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-special-regime`}>
            {t("settings.fiscalProfile.specialRegime", "Régimen especial de IVA")}
          </Label>
          <Controller
            name="specialRegime"
            control={control}
            render={({ field }) => (
              <>
                <Select
                  value={field.value || "none"}
                  onValueChange={(v) => field.onChange(v === "none" ? "" : v)}
                >
                  <SelectTrigger id={`${idPrefix}-special-regime`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {t("settings.fiscalProfile.specialRegimeNone", "Ninguno")}
                    </SelectItem>
                    <SelectItem value="automotoras_1222_008">
                      {t(
                        "settings.fiscalProfile.specialRegimeAutomotoras",
                        "Automotoras — venta de vehículos usados",
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {/* The ficto arithmetic and the citations only earn their space
                    once a régimen is actually chosen. Almost every tenant
                    leaves this at Ninguno, and two lines of fiscal detail under
                    an unset field is what made this card feel cluttered. */}
                {field.value ? (
                  <div className="rounded-[var(--fx-r-md)] bg-[var(--fx-s-tile)] px-3 py-2 text-xs text-muted-foreground">
                    <p>
                      {t(
                        "settings.fiscalProfile.specialRegimeHelp",
                        "Habilita las tasas 2,2% (vehículos) y 2,75% (motos) al facturar: IVA 22% sobre una base ficta del 10% del precio (12,5% en motos).",
                      )}
                    </p>
                    <p className="mt-1">
                      {t("settings.fiscalProfile.specialRegimeSources", "Normativa:")}{" "}
                      <NormativaLink href="https://www.impo.com.uy/bases/todgi2023/101-2024/14_T10">
                        Art. 14 Tít. 10 T.O. 2023
                      </NormativaLink>
                      {" · "}
                      <NormativaLink href="https://www.impo.com.uy/bases/decretos/220-998">
                        Art. 107 Dec. 220/998
                      </NormativaLink>
                      {" · "}
                      <NormativaLink href="https://www.gub.uy/direccion-general-impositiva/comunicacion/publicaciones/se-calcula-iva-caso-enajenacion-automotores-usados">
                        {t("settings.fiscalProfile.specialRegimeDgiPage", "DGI")}
                      </NormativaLink>
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "settings.fiscalProfile.specialRegimeHint",
                      "Solo para regímenes con tasa de IVA propia, como automotoras.",
                    )}
                  </p>
                )}
              </>
            )}
          />
        </div>

        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-foreground">
            {t(
              "settings.fiscalProfile.sections.specialRegimesTitle",
              "Leyendas de exoneración (opcional)",
            )}
          </CardTitle>
          <CardDescription>
            {t(
              "settings.fiscalProfile.sections.specialRegimesDescription",
              "Texto que se agrega a las facturas cuando la empresa está exonerada. Podés omitirlo si no aplica.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
        {hintKey ? (
          <Alert className="border-muted bg-muted/30">
            <AlertDescription className="text-sm text-muted-foreground">
              {t(`settings.fiscalProfile.${hintKey}`)}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="flex items-start space-x-2">
          <Checkbox
            id={`${idPrefix}-exoneration-enabled`}
            checked={hasExoneration}
            onCheckedChange={(c) => setValue("hasExoneration", c === true)}
            className="mt-0.5 transition-colors duration-150"
          />
          <div className="grid gap-0.5">
            <Label
              htmlFor={`${idPrefix}-exoneration-enabled`}
              className="cursor-pointer text-sm font-normal leading-tight"
            >
              {t("settings.exoneratedRegime.checkboxLabel")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("settings.fiscalProfile.sections.exonerationOptional")}
            </p>
          </div>
        </div>

        {hasExoneration && (
          <>
            <div className="space-y-2">
              <Label className="text-foreground">
                {t("settings.exoneratedRegime.legendSelectorLabel")}
              </Label>
              <Select value={dropdownValue} onValueChange={onLegendIdChange}>
                <SelectTrigger className="transition-colors duration-150">
                  <SelectValue
                    placeholder={t("settings.exoneratedRegime.legendSelectorLabel")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {options.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.legendText ?? t("settings.exoneratedRegime.legendOptions.custom")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {dropdownValue === "custom" && (
              <div className="space-y-2">
                <Label htmlFor={`${idPrefix}-exoneration-line`} className="text-foreground">
                  {t("settings.exoneratedRegime.legendLabel")}
                </Label>
                <Input
                  ref={legendInputRef}
                  id={`${idPrefix}-exoneration-line`}
                  type="text"
                  value={lineLegend}
                  onChange={(e) => {
                    setValue("exonerationLineLegend", e.target.value);
                    setValue("exonerationLegendId", "custom");
                  }}
                  placeholder={t("settings.exoneratedRegime.legendPlaceholder")}
                  className="transition-colors duration-150"
                />
                <p className="text-sm text-muted-foreground">
                  {t("settings.exoneratedRegime.legendHelper")}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-exoneration-header`} className="text-foreground">
                {t("settings.exoneratedRegime.headerLegendLabel")}
              </Label>
              <Controller
                name="exonerationHeaderLegend"
                control={control}
                render={({ field }) => (
                  <Input
                    id={`${idPrefix}-exoneration-header`}
                    type="text"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("settings.exoneratedRegime.headerLegendPlaceholder")}
                    className="transition-colors duration-150"
                  />
                )}
              />
            </div>
          </>
        )}
        </CardContent>
      </Card>
    </>
  );
}
